import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupee } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Ticket } from "lucide-react";

const BLANK = { code: "", type: "percentage", value: 10, min_order: 0, max_discount: 0, description: "", usage_limit: 0, active: true };

export default function AdminCoupons() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/coupons").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = () => {
    const payload = { ...editing, value: Number(editing.value), min_order: Number(editing.min_order), max_discount: Number(editing.max_discount), usage_limit: Number(editing.usage_limit) };
    const req = editing.id ? api.put(`/admin/coupons/${editing.id}`, payload) : api.post("/admin/coupons", payload);
    req.then(() => { toast.success("Saved"); setEditing(null); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Save failed"));
  };
  const del = (id) => { if (!window.confirm("Delete coupon?")) return; api.delete(`/admin/coupons/${id}`).then(() => { toast.success("Deleted"); load(); }); };
  const toggle = (c) => api.put(`/admin/coupons/${c.id}`, { ...c, active: !c.active }).then(load);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Coupons</h1>
        <Button onClick={() => setEditing({ ...BLANK })} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-coupon-btn"><Plus className="w-4 h-4 mr-1" /> Add Coupon</Button>
      </div>
      {!list ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl soft-shadow p-5" data-testid={`coupon-${c.id}`}>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 font-heading text-2xl text-forest"><Ticket className="w-5 h-5 text-terracotta" />{c.code}</span>
                <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`coupon-toggle-${c.id}`} />
              </div>
              <p className="text-sm text-brown/70 mt-2">{c.description}</p>
              <div className="text-sm text-olive mt-2">{c.type === "percentage" ? `${c.value}% off` : `${rupee(c.value)} off`} • Min {rupee(c.min_order)}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })} className="rounded-full flex-1 border-sage">Edit</Button>
                <button onClick={() => del(c.id)} className="w-9 h-9 rounded-full border border-sage flex items-center justify-center" data-testid={`delete-coupon-${c.id}`}><Trash2 className="w-4 h-4 text-terracotta" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{editing?.id ? "Edit" : "Add"} Coupon</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label className="text-sm">Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className="rounded-xl bg-white" data-testid="cf-code" /></div>
              <div><Label className="text-sm">Type</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="flat">Flat Amount</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Value</Label><Input type="number" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} className="rounded-xl bg-white" data-testid="cf-value" /></div>
                <div><Label className="text-sm">Max Discount</Label><Input type="number" value={editing.max_discount} onChange={(e) => setEditing({ ...editing, max_discount: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm">Min Order</Label><Input type="number" value={editing.min_order} onChange={(e) => setEditing({ ...editing, min_order: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm">Usage Limit</Label><Input type="number" value={editing.usage_limit} onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })} className="rounded-xl bg-white" /></div>
              </div>
              <div><Label className="text-sm">Description</Label><Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-xl bg-white" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-coupon-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
